import React, { useState, useEffect } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row, TabContent, TabPane, Button, Spinner } from 'reactstrap';
import classnames from 'classnames';
import { useNavigate, Link } from 'react-router-dom';

//import images
import bell from "../../assets/images/svg/bell.svg";

//SimpleBar
import SimpleBar from "simplebar-react";

// Import notification hooks and context
import { useNotifications, Notification } from '../Hooks/NotificationHooks';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useMessageHooks } from '../Hooks/MessageHooks';
import { getLoggedinUser } from '../../helpers/api_helper';
import webSocketService from '../../services/websocket.service';

const NotificationDropdown = () => {
    const navigate = useNavigate();
    const currentUser = getLoggedinUser();
    const userId = currentUser?.user?.id || currentUser?.data?.user?.id || currentUser?.id;
    
    //Dropdown Toggle
    const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
    };

    //Tab 
    const [activeTab, setActiveTab] = useState('1');
    const toggleTab = (tab : any) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    // Use notification hooks and context
    const { notifications, loading, error, actions } = useNotifications(undefined, false); // Disable auto-refresh in component
    const { totalUnreadCount, unreadCount, unreadMessageCount, isOnline, refreshCount } = useNotificationContext(); // Use context for unread count
    
    // Use message hooks to get recent messages
    const { groups: messageGroups, loading: messagesLoading, actions: messageActions } = useMessageHooks(userId);
    
    // Get recent messages from groups - show all groups with unread messages or recent activity
    const recentMessages = messageGroups
        .filter(group => group.unread_count > 0 || group.last_message)
        .sort((a, b) => {
            // Sort by last message time, but put unread messages first
            if (a.unread_count > 0 && b.unread_count === 0) return -1;
            if (a.unread_count === 0 && b.unread_count > 0) return 1;
            return new Date(b.last_message?.created_at || 0).getTime() - new Date(a.last_message?.created_at || 0).getTime();
        })
        .slice(0, 10); // Show last 10 conversations
    
    // Fetch groups on mount and when dropdown opens
    useEffect(() => {
        if (userId) {
            messageActions.fetchGroups();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]); // messageActions excluded to prevent infinite loops
    
    // Fetch groups when switching to Messages tab
    useEffect(() => {
        if (isNotificationDropdown && activeTab === '2' && currentUser?.id) {
            messageActions.fetchGroups();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNotificationDropdown, activeTab, currentUser?.id]); // messageActions excluded to prevent infinite loops
    
    // Listen for new messages to update the groups list
    useEffect(() => {
        if (!userId) return;

        const handleNewMessage = (message: any) => {
            // Refresh groups to update last message and unread counts
            messageActions.fetchGroups();
        };

        const handleBadgeUpdate = () => {
            // Refresh groups when badge updates
            messageActions.fetchGroups();
        };

        // Listen to all groups for new messages
        webSocketService.onMessage('*', handleNewMessage);

        // Also listen for badge updates
        const socket = (webSocketService as any).socket;
        if (socket) {
            socket.on('badge_update', handleBadgeUpdate);
        }

        return () => {
            webSocketService.offMessage('*', handleNewMessage);
            if (socket) {
                socket.off('badge_update', handleBadgeUpdate);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]); // messageActions excluded to prevent infinite loops

    // Helper function to get notification icon based on type
    const getNotificationIcon = (type: string) => {
        if (type.includes('mention')) return 'bx bx-at';
        if (type.includes('WIPVerrechnen')) return 'bx bx-receipt';
        if (type.includes('Message')) return 'bx bx-message-square-dots';
        if (type.includes('Alert')) return 'bx bx-bell';
        return 'bx bx-info-circle';
    };

    // Helper function to get notification color based on type
    const getNotificationColor = (type: string) => {
        if (type.includes('mention')) return 'bg-info-subtle text-info';
        if (type.includes('WIPVerrechnen')) return 'bg-success-subtle text-success';
        if (type.includes('Message')) return 'bg-primary-subtle text-primary';
        if (type.includes('Alert')) return 'bg-warning-subtle text-warning';
        return 'bg-info-subtle text-info';
    };

    // Format time
    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
        if (diffInHours < 24) return `${diffInHours} hr${diffInHours > 1 ? 's' : ''} ago`;
        if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    // Handle notification click
    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read if unread
        if (!notification.is_read) {
            const success = await actions.markAsRead(notification.id);
            if (success) {
                // Refresh the context count immediately
                refreshCount();
            }
        }

        // Navigate based on notification type
        if (notification.type === 'mention' && notification.data.project_id) {
            // Close dropdown
            setIsNotificationDropdown(false);
            // Navigate to project with comments tab
            navigate(`/projects/${notification.data.project_id}?tab=activities&comment=${notification.data.comment_id}`);
        }
        // Add other notification type handlers here as needed
    };

    // Handle mark all as read
    const handleMarkAllAsRead = async () => {
        const success = await actions.markAllAsRead();
        if (success) {
            // Refresh the context count immediately
            refreshCount();
            // Also refresh the local notifications list
            actions.refresh();
        }
    };

    // Filter notifications by type for tabs
    const allNotifications = notifications;
    const messageNotifications = notifications.filter(n => n.type.includes('Message'));
    const alertNotifications = notifications.filter(n => 
        n.type.includes('Alert') || n.type.includes('WIPVerrechnen')
    );

    // Get current tab notifications
    const getCurrentTabNotifications = () => {
        switch (activeTab) {
            case '2': return messageNotifications;
            case '3': return alertNotifications;
            default: return allNotifications;
        }
    };

    const currentNotifications = getCurrentTabNotifications();
    
    // Handle message click
    const handleMessageClick = (groupId: number) => {
        setIsNotificationDropdown(false);
        navigate(`/messages?group=${groupId}`);
    };
    return (
        <React.Fragment>
            <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
                    <i className='bx bx-bell fs-22'></i>
                    {totalUnreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {totalUnreadCount > 99 ? '99+' : String(totalUnreadCount)}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                        <div className="p-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white">Notifications</h6>
                                </Col>
                                <div className="col-auto dropdown-tabs">
                                    <span className="badge bg-light-subtle fs-13 text-body">
                                        {String(totalUnreadCount)} New
                                    </span>
                                    {!isOnline && (
                                        <span className="badge bg-warning-subtle text-warning fs-11 ms-2">
                                            Offline
                                        </span>
                                    )}
                                    {unreadCount > 0 && (
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-white text-decoration-none ms-2"
                                            onClick={handleMarkAllAsRead}
                                            disabled={loading || !isOnline}
                                        >
                                            Mark all read
                                        </Button>
                                    )}
                                </div>
                            </Row>
                        </div>

                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '1' })}
                                        onClick={() => { toggleTab('1'); }}
                                    >
                                        All ({allNotifications.filter(n => !n.is_read).length})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '2' })}
                                        onClick={() => { toggleTab('2'); }}
                                    >
                                        Messages ({unreadMessageCount})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '3' })}
                                        onClick={() => { toggleTab('3'); }}
                                    >
                                        Alerts ({alertNotifications.filter(n => !n.is_read).length})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId={activeTab} className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {/* Messages Tab Content */}
                                {activeTab === '2' ? (
                                    <>
                                        {messagesLoading && (
                                            <div className="text-center py-3">
                                                <Spinner size="sm" />
                                                <span className="ms-2">Loading messages...</span>
                                            </div>
                                        )}
                                        
                                        {!messagesLoading && recentMessages.length === 0 && (
                                            <div className="text-center py-4">
                                                <div className="w-25 w-sm-50 pt-3 mx-auto">
                                                    <img src={bell} className="img-fluid" alt="no-messages" />
                                                </div>
                                                <div className="text-center pb-3 mt-2">
                                                    <h6 className="fs-14 fw-semibold lh-base">
                                                        No messages yet
                                                    </h6>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {!messagesLoading && recentMessages.map((group) => (
                                            <div 
                                                key={group.id}
                                                className={`text-reset notification-item d-block dropdown-item position-relative ${group.unread_count > 0 ? 'active' : ''}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleMessageClick(group.id)}
                                            >
                                                <div className="d-flex">
                                                    <div className="avatar-xs me-3">
                                                        {group.is_direct_message && group.other_user_avatar ? (
                                                            <img 
                                                                src={group.other_user_avatar} 
                                                                alt={group.name}
                                                                className="avatar-xs rounded-circle"
                                                            />
                                                        ) : (
                                                            <span className={`avatar-title rounded-circle fs-16 ${
                                                                group.is_direct_message ? 'bg-primary-subtle text-primary' : 'bg-success-subtle text-success'
                                                            }`}>
                                                                {group.is_direct_message ? (
                                                                    group.name.charAt(0).toUpperCase()
                                                                ) : (
                                                                    <i className="ri-group-line"></i>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className={`mt-0 mb-1 lh-base fs-13 ${group.unread_count > 0 ? 'fw-semibold' : ''}`}>
                                                            {group.name}
                                                        </h6>
                                                        <p className="mb-1 fs-12 text-muted text-truncate">
                                                            {group.last_message ? group.last_message.content : 'No messages yet'}
                                                        </p>
                                                        <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                            <span>
                                                                <i className="mdi mdi-clock-outline"></i> {group.last_message ? formatTime(group.last_message.created_at) : 'Never'}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    {group.unread_count > 0 && (
                                                        <div className="px-2">
                                                            <span className="badge badge-soft-danger rounded-pill">
                                                                {group.unread_count}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    /* Regular Notifications Content */
                                    <>
                                        {loading && (
                                            <div className="text-center py-3">
                                                <Spinner size="sm" />
                                                <span className="ms-2">Loading notifications...</span>
                                            </div>
                                        )}
                                        
                                        {error && (
                                            <div className="text-center py-3">
                                                <p className="text-danger mb-2">{error}</p>
                                                <Button size="sm" color="primary" onClick={actions.refresh}>
                                                    Retry
                                                </Button>
                                            </div>
                                        )}

                                        {!loading && !error && currentNotifications.length === 0 && (
                                            <div className="text-center py-4">
                                                <div className="w-25 w-sm-50 pt-3 mx-auto">
                                                    <img src={bell} className="img-fluid" alt="no-notifications" />
                                                </div>
                                                <div className="text-center pb-3 mt-2">
                                                    <h6 className="fs-14 fw-semibold lh-base">
                                                        {activeTab === '3' ? 'No alerts yet' : 'No notifications yet'}
                                                    </h6>
                                                </div>
                                            </div>
                                        )}

                                        {!loading && !error && currentNotifications.map((notification, index) => (
                                    <div 
                                        key={notification.id}
                                        className={`text-reset notification-item d-block dropdown-item position-relative ${!notification.is_read ? 'active' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="d-flex">
                                            <div className="avatar-xs me-3">
                                                <span className={`avatar-title ${getNotificationColor(notification.type)} rounded-circle fs-16`}>
                                                    <i className={getNotificationIcon(notification.type)}></i>
                                                </span>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className={`mt-0 mb-2 lh-base fs-13 ${!notification.is_read ? 'fw-semibold' : ''}`}>
                                                    {notification.data.title}
                                                </h6>
                                                <p className="mb-1 fs-12 text-muted">
                                                    {notification.data.content || notification.data.message}
                                                </p>
                                                <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                    <span>
                                                        <i className="mdi mdi-clock-outline"></i> {formatTime(notification.created_at)}
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="px-2 fs-15">
                                                <div className="form-check notification-check">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        checked={notification.is_read}
                                                        onChange={() => {}}
                                                        id={`notification-check-${notification.id}`}
                                                    />
                                                    <label className="form-check-label" htmlFor={`notification-check-${notification.id}`}></label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                        {!loading && !error && currentNotifications.length > 0 && (
                                            <div className="my-3 text-center">
                                                <Button 
                                                    color="soft-success" 
                                                    size="sm"
                                                    className="waves-effect waves-light"
                                                    onClick={actions.refresh}
                                                >
                                                    Refresh <i className="ri-refresh-line align-middle"></i>
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </SimpleBar>
                            
                            {/* Link to full notifications/messages page */}
                            <div className="notification-actions border-top p-2">
                                {activeTab === '2' ? (
                                    <Link to="/messages" className="text-decoration-none">
                                        <div className="d-grid">
                                            <Button color="soft-primary" size="sm">
                                                <i className="ri-message-3-line me-1"></i>
                                                Alle Nachrichten anzeigen
                                            </Button>
                                        </div>
                                    </Link>
                                ) : (
                                    <Link to="/notifications" className="text-decoration-none">
                                        <div className="d-grid">
                                            <Button color="soft-primary" size="sm">
                                                <i className="ri-notification-3-line me-1"></i>
                                                Alle Benachrichtigungen anzeigen
                                            </Button>
                                        </div>
                                    </Link>
                                )}
                            </div>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;